import React, { useEffect, useState } from "react";
import { Button, Modal, message, Tag, Select } from "antd";
import { Form, InputGroup, Badge } from "react-bootstrap";
import {
    UserOutlined,
    EditOutlined,
    DeleteOutlined,
    PhoneOutlined,
    MailOutlined,
    PlusOutlined,
    EnvironmentOutlined
} from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";
import Paginationnew from "../../Uitiles/Paginationnew";

const DiagnosticPatients = () => {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPatient, setEditingPatient] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        age: '',
        gender: 'Male',
        phone: '',
        email: '',
        address: ''
    });

    useEffect(() => {
        loadPatients();
    }, [page, searchText]);

    const loadPatients = async () => {
        try {
            setLoading(true);
            const userId = await getuserdata('id');
            let conditions = { diagnostic_id: userId };

            const search = searchText ? {
                name: searchText,
                phone: searchText
            } : {};
            const pagination = { page, limit };

            const response = await oneKlicks.read('', 'diagnostic_patients', conditions, 'id DESC', search, pagination);
            if (response && response.data) {
                setPatients(response.data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (error) {
            console.error('Error loading patients:', error);
            message.error('Failed to load patients');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (!formData.name || !formData.phone) {
                message.error('Name and Phone are required');
                return;
            }

            const userId = await getuserdata('id');
            const patientData = {
                ...formData,
                diagnostic_id: userId,
                age: formData.age || 0
            };

            if (editingPatient) {
                await oneKlicks.update('', 'diagnostic_patients', patientData, { id: editingPatient.id });
                message.success('Patient updated successfully');
            } else {
                await oneKlicks.create('', 'diagnostic_patients', {
                    ...patientData,
                    created_at: new Date().toISOString()
                });
                message.success('Patient added successfully');
            }

            setModalVisible(false);
            resetForm();
            loadPatients();
        } catch (error) {
            console.error('Error saving patient:', error);
            message.error('Failed to save patient');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            age: '',
            gender: 'Male',
            phone: '',
            email: '',
            address: ''
        });
        setEditingPatient(null);
    };

    const handleEdit = (patient) => {
        setEditingPatient(patient);
        setFormData({
            name: patient.name || '',
            age: patient.age || '',
            gender: patient.gender || 'Male',
            phone: patient.phone || '',
            email: patient.email || '',
            address: patient.address || ''
        });
        setModalVisible(true);
    };

    const handleDelete = async (patient) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete patient "${patient.name}"?`);
        if (confirmDelete) {
            try {
                await oneKlicks.delete('', 'diagnostic_patients', { id: patient.id });
                message.success('Patient deleted successfully');
                loadPatients();
            } catch (error) {
                console.error('Error deleting patient:', error);
                message.error('Failed to delete patient');
            }
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    return (
        <div className="container-fluid py-3">
            <div className="card mb-5">
                <div className="card-body">
                    <div className="align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h5 className="mb-0 font-weight-bold mr-3">
                                <UserOutlined className="me-2 text-success" />
                                Patients Management
                            </h5>
                            <Badge bg="success">Patients</Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                            <InputGroup className="flex-grow-1 me-2 h-40" style={{ maxWidth: '300px' }}>
                                <Form.Control
                                    placeholder="Search by name, phone..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </InputGroup>
                            <Button className="btn-primary h-40" onClick={() => {
                                resetForm();
                                setModalVisible(true);
                            }}>
                                <PlusOutlined /> Add Patient
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Patient</th>
                                    <th>Age/Gender</th>
                                    <th>Contact</th>
                                    <th>Address</th>
                                    <th style={{ width: '150px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">Loading...</td>
                                    </tr>
                                ) : patients.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">No patients found</td>
                                    </tr>
                                ) : (
                                    patients.map((item) => (
                                        <tr key={item.id}>
                                            <td data-label="Patient">
                                                <strong>{item.name}</strong>
                                            </td>
                                            <td data-label="Age/Gender">
                                                <div className="d-flex align-items-center gap-2">
                                                    <Badge bg="secondary">{item.age} Yrs</Badge>
                                                    <Tag color={item.gender === 'Male' ? 'blue' : 'pink'}>{item.gender}</Tag>
                                                </div>
                                            </td>
                                            <td data-label="Contact">
                                                <div className="d-flex flex-column">
                                                    <span><PhoneOutlined className="me-1 text-muted" /> {item.phone}</span>
                                                    {item.email && <span className="small text-muted"><MailOutlined className="me-1" /> {item.email}</span>}
                                                </div>
                                            </td>
                                            <td data-label="Address">
                                                <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    <EnvironmentOutlined className="me-1 text-muted" />{item.address || '-'}
                                                </div>
                                            </td>
                                            <td data-label="Actions">
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                    <Button
                                                        className="btn-info"
                                                        onClick={() => handleEdit(item)}
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        <EditOutlined />
                                                    </Button>
                                                    <Button
                                                        className="btn-danger"
                                                        onClick={() => handleDelete(item)}
                                                        style={{ fontSize: '12px', padding: '4px 8px' }}
                                                    >
                                                        <DeleteOutlined />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Paginationnew currentPage={page} totalPages={totalPages} handlePageChange={handlePageChange} />
                </div>
            </div>

            {/* Add/Edit Modal */}
            <Modal
                title={editingPatient ? 'Edit Patient' : 'Add New Patient'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    resetForm();
                }}
                onOk={handleSubmit}
                width={700}
            >
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Patient Name *</label>
                        <Form.Control
                            placeholder="Enter patient name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Phone *</label>
                        <Form.Control
                            placeholder="Enter phone number"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                    </div>
                    <div className="col-md-4 mb-3">
                        <label className="form-label">Age</label>
                        <Form.Control
                            type="number"
                            placeholder="Age"
                            value={formData.age}
                            onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        />
                    </div>
                    <div className="col-md-4 mb-3">
                        <label className="form-label">Gender</label>
                        <Select
                            style={{ width: '100%' }}
                            value={formData.gender}
                            onChange={(value) => setFormData({ ...formData, gender: value })}
                            options={[
                                { value: 'Male', label: 'Male' },
                                { value: 'Female', label: 'Female' },
                                { value: 'Other', label: 'Other' }
                            ]}
                        />
                    </div>
                    <div className="col-md-4 mb-3">
                        <label className="form-label">Email</label>
                        <Form.Control
                            type="email"
                            placeholder="Enter email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                    </div>
                    <div className="col-12 mb-3">
                        <label className="form-label">Address</label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Enter address..."
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DiagnosticPatients;
